import { UserRole } from '../apiClients/workspace/types';

/**
 * Available emoji options for skill levels
 */
export const SKILL_LEVEL_EMOJIS = [
  { id: 'seedling', emoji: '🌱', name: 'Seedling' },
  { id: 'star', emoji: '⭐', name: 'Star' },
  { id: 'fire', emoji: '🔥', name: 'Fire' },
  { id: 'diamond', emoji: '💎', name: 'Diamond' },
  { id: 'crown', emoji: '👑', name: 'Crown' },
  { id: 'rocket', emoji: '🚀', name: 'Rocket' },
  { id: 'trophy', emoji: '🏆', name: 'Trophy' },
  { id: 'medal', emoji: '🏅', name: 'Medal' },
  { id: 'gem', emoji: '💠', name: 'Gem' },
  { id: 'lightning', emoji: '⚡', name: 'Lightning' },
];

/**
 * Get default emoji by weight
 */
export const getDefaultEmojiByWeight = (weight: number): string => {
  const defaultEmojis = [
    SKILL_LEVEL_EMOJIS[0], // seedling for weight 1
    SKILL_LEVEL_EMOJIS[1], // star for weight 2
    SKILL_LEVEL_EMOJIS[2], // fire for weight 3
    SKILL_LEVEL_EMOJIS[3], // diamond for weight 4
    SKILL_LEVEL_EMOJIS[4], // crown for weight 5
  ];

  const emojiData = defaultEmojis[weight - 1] || defaultEmojis[0];
  return emojiData.emoji;
};

/**
 * Get default iconId by weight
 */
export const getDefaultIconIdByWeight = (weight: number): string => {
  const defaultEmojis = [
    SKILL_LEVEL_EMOJIS[0], // seedling for weight 1
    SKILL_LEVEL_EMOJIS[1], // star for weight 2
    SKILL_LEVEL_EMOJIS[2], // fire for weight 3
    SKILL_LEVEL_EMOJIS[3], // diamond for weight 4
    SKILL_LEVEL_EMOJIS[4], // crown for weight 5
  ];

  const emojiData = defaultEmojis[weight - 1] || defaultEmojis[0];
  return emojiData.id;
};

/**
 * Get emoji by iconId
 */
export const getEmojiById = (iconId?: string): string => {
  if (!iconId) return '🌱';
  const found = SKILL_LEVEL_EMOJIS.find((item) => item.id === iconId);
  return found ? found.emoji : '🌱';
};

/**
 * Get emoji icon for skill level based on weight or iconId
 */
export const getLevelIcon = (
  weightOrIconId: number | string | null | undefined,
  iconId?: string
): string => {
  if (!weightOrIconId) return '❓';

  // If iconId is provided, use it
  if (typeof weightOrIconId === 'string') {
    return getEmojiById(weightOrIconId);
  }

  // If iconId is provided as second parameter, use it
  if (iconId) {
    return getEmojiById(iconId);
  }

  // Use default emoji for weight
  const weight = weightOrIconId as number;
  return getDefaultEmojiByWeight(weight);
};

/**
 * Get user role display text key for translation
 */
export const getRoleDisplayKey = (roles: UserRole[]): string => {
  if (roles.includes(UserRole.Admin)) return 'team.roleAdmin';
  if (roles.includes(UserRole.Participant)) return 'team.roleMember';
  return 'team.roleMember';
};

/**
 * Check if user has admin role
 */
export const isUserAdmin = (roles: UserRole[]): boolean => {
  return roles.includes(UserRole.Admin);
};

/**
 * Get primary role for user (highest priority role)
 */
export const getPrimaryRole = (roles: UserRole[]): UserRole => {
  if (roles.includes(UserRole.Admin)) return UserRole.Admin;
  if (roles.includes(UserRole.Participant)) return UserRole.Participant;
  return UserRole.Guest;
};

/**
 * Generate mock user statistics
 * TODO: Replace with real data from API
 */
export interface UserStats {
  trainingsAttended: number;
  trainingsThisMonth: number;
  averageRating?: number;
  joinedDate?: string;
}

export const getMockUserStats = (userId: string): UserStats => {
  // Use userId as seed for consistent mock data
  const seed = userId.charCodeAt(0) + userId.charCodeAt(userId.length - 1);

  return {
    trainingsAttended: Math.floor((seed % 50) + 5),
    trainingsThisMonth: Math.floor((seed % 8) + 1),
    averageRating: Number(((seed % 40) / 10 + 3).toFixed(1)), // 3.0 - 7.0 range
    joinedDate: '2024-01-01', // Mock date
  };
};
